import React, { useState, useEffect } from 'react';
import { useActivityTemplates } from '../../../activity-creator/hooks/useActivityTemplates';
import { pendingActivitiesApi } from '../../api/guardians';
import { useTranslation } from '../../../../shared/i18n';

/**
 * WizardActivitySelectionStep - Multi-select activity templates for the wizard
 * Allows guardian to select multiple activities upfront, then configure each one
 */
export const WizardActivitySelectionStep = ({
  childId,
  childName,
  initialSelectedTemplates = [],
  alreadyConfiguredTemplates = [],
  onContinue,
  onSkip,
  onBack
}) => {
  const { t } = useTranslation();
  // Initialize with any previously selected templates (pending + active)
  const [selectedTemplates, setSelectedTemplates] = useState(initialSelectedTemplates);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all templates
  const { templates, isLoading, error: templatesError } = useActivityTemplates(childId);

  // Update selected templates when initial values change (e.g., when going back)
  useEffect(() => {
    setSelectedTemplates(initialSelectedTemplates);
  }, [initialSelectedTemplates]);

  // Check if a template is already configured (active)
  const isAlreadyConfigured = (templateKey) => alreadyConfiguredTemplates.includes(templateKey);

  // Check if a template is selectable (based on subscription status and free settings)
  const isTemplateSelectable = (template) => {
    // Use the selectable field from the API if available
    if (template.selectable !== undefined) {
      return template.selectable;
    }
    // Default to true for backwards compatibility
    return true;
  };

  const handleToggleTemplate = (template) => {
    const templateKey = template.key;
    // Don't allow toggling already configured activities
    if (isAlreadyConfigured(templateKey)) {
      return;
    }
    // Don't allow toggling non-selectable activities
    if (!isTemplateSelectable(template)) {
      return;
    }

    setSelectedTemplates(prev => {
      if (prev.includes(templateKey)) {
        return prev.filter(k => k !== templateKey);
      }
      return [...prev, templateKey];
    });
  };

  // Calculate templates that need configuration (selected but not already active)
  const templatesToConfigure = selectedTemplates.filter(key => !isAlreadyConfigured(key));

  const handleContinue = async () => {
    if (templatesToConfigure.length === 0) {
      // All selected activities are already configured, go to goal step
      onSkip();
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Save only the new templates as pending activities (not the already configured ones)
      await pendingActivitiesApi.bulkUpdate(childId, templatesToConfigure);

      onContinue(templatesToConfigure);
    } catch (err) {
      setError(err.message || 'Failed to save activity selection');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Loading activity templates...</p>
      </div>
    );
  }

  if (templatesError) {
    return (
      <div className="alert alert-danger">
        <h5 className="alert-heading">Error Loading Templates</h5>
        <p>{templatesError}</p>
      </div>
    );
  }

  return (
    <div>
      <h4 className="mb-3" data-testid="wizard-step-title">Chọn Hoạt Động</h4>
      <p className="text-muted mb-4">
        Chọn hoạt động học tập cho {childName}
      </p>

      {error && (
        <div className="alert alert-danger mb-3">
          {error}
        </div>
      )}

      {/* Activity selection list */}
      <div className="list-group mb-4">
        {templates.map((template) => {
          const isSelected = selectedTemplates.includes(template.key);
          const isConfigured = isAlreadyConfigured(template.key);
          const isSelectable = isTemplateSelectable(template);
          const isDisabled = isConfigured || !isSelectable;

          return (
            <div
              key={template.key}
              className={`list-group-item list-group-item-action d-flex align-items-center py-3 ${
                isSelected ? 'active' : ''
              } ${!isSelectable ? 'opacity-50' : ''}`}
              onClick={() => handleToggleTemplate(template)}
              style={{ cursor: isDisabled ? 'default' : 'pointer' }}
              data-testid={`wizard-activity-${template.key}`}
              data-selectable={isSelectable ? 'true' : 'false'}
            >
              {/* Activity Icon */}
              <div className="flex-shrink-0 me-3">
                <div
                  className="rounded-2 overflow-hidden"
                  style={{
                    width: '40px',
                    height: '40px',
                    filter: !isSelectable ? 'grayscale(100%)' : 'none'
                  }}
                >
                  <img
                    src={template.icon_url}
                    alt={template.name}
                    className="w-100 h-100 object-fit-cover"
                    onError={(e) => {
                      e.target.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Crect width='40' height='40' fill='%23e9ecef'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='16' fill='%236c757d'%3E${template.name[0]}%3C/text%3E%3C/svg%3E`;
                    }}
                  />
                </div>
              </div>

              {/* Activity Name and Description */}
              <div className="flex-grow-1">
                <div className="d-flex align-items-center flex-wrap gap-1">
                  <span className="fw-medium">{template.name}</span>
                  {isConfigured && (
                    <span className="badge bg-success">Đã cấu hình</span>
                  )}
                  {!template.free && (
                    <span
                      className="badge bg-warning text-dark"
                      style={{ filter: !isSelectable ? 'grayscale(100%)' : 'none' }}
                      data-testid={`pro-badge-${template.key}`}
                    >
                      <i className="bi bi-star-fill me-1"></i>
                      {t('common.badge_pro')}
                    </span>
                  )}
                  {template.free && (
                    <span
                      className="badge bg-success"
                      style={{ filter: !isSelectable ? 'grayscale(100%)' : 'none' }}
                      data-testid={`free-badge-${template.key}`}
                    >
                      {t('common.badge_free')}
                    </span>
                  )}
                  {/* Display tags (extra_cost, tablet_required) as badges */}
                  {template.tags && template.tags.map((tagName, index) => {
                    const colors = template.tag_colors?.[tagName] || { bg: 'bg-secondary', text: 'text-white' };
                    return (
                      <span
                        key={index}
                        className={`badge ${colors.bg} ${colors.text}`}
                        data-testid={`activity-tag-${template.key}`}
                      >
                        {tagName}
                      </span>
                    );
                  })}
                </div>
                <small className={`d-block ${isSelected ? 'text-white-50' : 'text-muted'}`}>
                  {template.description}
                </small>
              </div>

              {/* Checkbox */}
              <div className="flex-shrink-0">
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '4px',
                    border: isSelected ? '2px solid white' : '2px solid #dee2e6',
                    backgroundColor: isSelected ? 'var(--bs-primary)' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {isSelected && (
                    <i className="bi bi-check text-white" style={{ fontSize: '16px' }}></i>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Selected count */}
      {selectedTemplates.length > 0 && (
        <div className="alert alert-info mb-4" data-testid="selected-count">
          <i className="bi bi-info-circle me-2"></i>
          {templatesToConfigure.length === selectedTemplates.length ? (
            `Đã chọn ${selectedTemplates.length} hoạt động. Bước tiếp theo sẽ cấu hình từng hoạt động.`
          ) : templatesToConfigure.length === 0 ? (
            `Đã chọn ${selectedTemplates.length} hoạt động (tất cả đã được cấu hình).`
          ) : (
            `Đã chọn ${selectedTemplates.length} hoạt động (${templatesToConfigure.length} cần cấu hình).`
          )}
        </div>
      )}

      {/* Action buttons */}
      <div className="d-flex justify-content-between">
        <div className="d-flex gap-2">
          {onBack && (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onBack}
              disabled={isSubmitting}
            >
              {t('common.back')}
            </button>
          )}
          <a
            href="#"
            className="btn btn-link text-muted"
            onClick={(e) => { e.preventDefault(); onSkip(); }}
            style={{ pointerEvents: isSubmitting ? 'none' : 'auto', opacity: isSubmitting ? 0.5 : 1 }}
          >
            Bỏ Qua
          </a>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleContinue}
          disabled={isSubmitting}
          data-testid="continue-btn"
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Đang xử lý...
            </>
          ) : selectedTemplates.length > 0 ? (
            `Tiếp Tục (${selectedTemplates.length} hoạt động)`
          ) : (
            'Tiếp Tục'
          )}
        </button>
      </div>
    </div>
  );
};
